/**
 * How large a price reduction this product is willing to believe.
 *
 * ONE definition, because there were two and they disagreed.
 *
 *   web/src/lib/homeFeed.ts        MAX_CREDIBLE_CUT_SHARE   = 0.5   (50%)
 *   web/src/components/listing/…  MAX_CREDIBLE_CUT_PERCENT = 60
 *   internal/notifications/price_cut_pass.go  MaxCredibleCutPercent = 60
 *
 * Three surfaces, two answers, and the 50–60% band was therefore a reduction the
 * notifier would email you about, the price-journey chart would render without
 * comment, and the browse rails would hide as a data error. All three describe
 * the same listing.
 *
 * MEASURED, 2026-08-06, on the live staging feed: 4,163 listings scanned, 1,517
 * carrying a reduction. Median 5.9%, p90 14.3%, max 57.5%. Twelve exceed 30%,
 * three exceed 50%, and NOTHING exceeds 60% — so the notifier's ceiling has never
 * rejected anything, and the entire practical disagreement is three listings:
 *
 *   57.5%  Los Gatos        4,810 sqft, 5 bd   $3,995,000 → $1,698,000
 *   51.9%  Novato               1 sqft, 2 bd  $24,950,000 → $12,000,000
 *   50.6%  East Palo Alto       1 sqft, 1 bd   $3,950,000 → $1,950,000
 *
 * Two of the three carry `sqft: 1` and are already rejected by MIN_CREDIBLE_SQFT,
 * which is the guard that actually catches the malformed records. The third is a
 * complete, well-formed listing. So the 50% bar's only independent effect on this
 * feed is to suppress one real luxury reprice, while the typo-catching it was
 * credited with is done by the sqft and price floors.
 *
 * That also retires the claim the 0.5 was justified by — "No seller in this
 * market cuts half the asking price. Anything past that is a correction to the
 * record, not a reduction in the home." Three did, and the one that survives the
 * sqft floor is not a correction to anything: 4,810 sqft, five bedrooms, Los
 * Gatos. The dropped-digit example that comment cites ($580,000 → $58,000, a 90%
 * cut) does not appear anywhere in the current feed.
 *
 * So: 60, matching the Go constant, which is the number with a second
 * implementation to disagree with it if it ever drifts.
 *
 * WHAT THIS IS NOT. It is not a claim that a 59% reduction is a good deal, and it
 * is not the only credibility guard — MIN_CREDIBLE_PRICE and MIN_CREDIBLE_SQFT
 * do the heavier work and stay exactly as they are. It is the outer bound past
 * which a number stops being a price and starts being a typo.
 */
export const MAX_CREDIBLE_CUT_PERCENT = 60;

/** The same bound as a fraction, for callers holding a ratio rather than a percent. */
export const MAX_CREDIBLE_CUT_SHARE = MAX_CREDIBLE_CUT_PERCENT / 100;
